// GAVION packages, MRR and commissions (spec docs/superpowers/specs/2026-10-20-packages-commissions-design.md §1–§6).
// Pure (no remote import): the TS twin of private.subscription_mrr, private.commission_lines and
// private.commission_statement_fill (supabase/schemas/70_gavion_commissions.sql), used by the FakeRest demo and the
// screens. The vectors of commissions.test.ts are those of supabase/tests/commissions_lines.test.sql. Money is CAD in
// dollars with at most 2 decimals; every computation runs on integer cents (BigInt products), rounded half-up per line
// like Postgres round(x, 2). Months are « YYYY-MM-01 » strings; dates « YYYY-MM-DD »; instants ISO strings.
// scripts/gavion/opsParity.test.ts pins the seed, the sources and the texts against the SQL.

import {
  holdsAmountRole,
  type DealRoleFields,
  type DealViewer,
} from "./dealRoles.ts";

export const SALE_SOURCES = [
  "rep_appointment",
  "inbound",
  "referral",
  "assigned",
] as const;
export type SaleSource = (typeof SALE_SOURCES)[number];
/** 75_ (owner ruling 2026-10-21): « organique » (inbound) is eligible too, paid like a client confié. */
export const DEFAULT_ELIGIBLE_SOURCES: readonly SaleSource[] = [
  "rep_appointment",
  "assigned",
  "inbound",
];
/** Amendment art. 4.5.4 (owner ruling 2026-10-21): a client confié (assigned) or organique (inbound — the client came by
 * himself, no rep obtained the appointment): no setter share, no bonus, no « À décider »; the Closer earns his full
 * share when he conducted the demo, demo_by_other_split_pct when someone else did. */
export const isClientSource = (source: string | null): boolean =>
  source === "assigned" || source === "inbound";
export const PACKAGE_LINES = ["reception", "admin_assistant"] as const;
export type PackageLine = (typeof PACKAGE_LINES)[number];
export const PACKAGE_TIERS = ["essentiel", "avance", "entreprise"] as const;
export type PackageTier = (typeof PACKAGE_TIERS)[number];
export const ADDON_BILLINGS = ["monthly", "per_unit_in_month"] as const;
export type AddonBilling = (typeof ADDON_BILLINGS)[number];
export const BILLINGS = ["monthly", "annual"] as const;
export type Billing = (typeof BILLINGS)[number];
export const EXCEPTION_KINDS = ["unpaid", "refunded", "partial"] as const;
export type ExceptionKind = (typeof EXCEPTION_KINDS)[number];
export type EarningKind = "commission" | "bonus";
/** « À décider » (art. 4.5.6, spec « Corrections »): no amount; on no statement (not paid, not lost) until the split is
 * recorded, and no longer a blocker of « Marquer versé » (owner ruling 2026-10-21, 75_). */
export type LineKind = EarningKind | "adjustment" | "to_decide";
export const DEFAULT_ANNUAL_DISCOUNT_PCT = 15;
/** Art. 5.2 b: an appointment taken before 2026-10-20 00:00 America/Toronto (EDT) need not precede the demo. */
export const APPOINTMENT_RULE_FROM = "2026-10-20T04:00:00.000Z";

/** gavion.ai/pricing, read 2026-10-20 (SQL seed of 70 §2). */
export const SEED_PACKAGES = [
  {
    code: "reception_essentiel",
    name: "Réceptionniste — Essentiel",
    line: "reception",
    tier: "essentiel",
    monthly_price: 129,
    position: 1,
  },
  {
    code: "reception_avance",
    name: "Réceptionniste — Avancé",
    line: "reception",
    tier: "avance",
    monthly_price: 249,
    position: 2,
  },
  {
    code: "reception_entreprise",
    name: "Réceptionniste — Entreprise",
    line: "reception",
    tier: "entreprise",
    monthly_price: 439,
    position: 3,
  },
  {
    code: "admin_assistant_essentiel",
    name: "Adjointe administrative — Essentiel",
    line: "admin_assistant",
    tier: "essentiel",
    monthly_price: 279,
    position: 4,
  },
  {
    code: "admin_assistant_avance",
    name: "Adjointe administrative — Avancé",
    line: "admin_assistant",
    tier: "avance",
    monthly_price: 429,
    position: 5,
  },
  {
    code: "admin_assistant_entreprise",
    name: "Adjointe administrative — Entreprise",
    line: "admin_assistant",
    tier: "entreprise",
    monthly_price: 659,
    position: 6,
  },
] as const;
export const SEED_ADDONS = [
  {
    code: "extra_user_reception",
    name: "Utilisateur supplémentaire (Réceptionniste)",
    line: "reception",
    unit_price: 22,
    billing: "monthly",
    position: 1,
  },
  {
    code: "extra_user_admin_assistant",
    name: "Utilisateur supplémentaire (Adjointe administrative)",
    line: "admin_assistant",
    unit_price: 39,
    billing: "monthly",
    position: 2,
  },
  {
    code: "extra_voice_agent",
    name: "Agent vocal supplémentaire",
    line: null,
    unit_price: 10,
    billing: "monthly",
    position: 3,
  },
  {
    code: "extra_quote",
    name: "Soumission supplémentaire",
    line: null,
    unit_price: 8,
    billing: "per_unit_in_month",
    position: 4,
  },
] as const;

export type AddonSnapshot = { code: string; qty: number; unit_price: number };
/** A recurring custom service (integration, new product, custom work billed every month): part of the base. */
export type RecurringService = { name: string; amount: number };
export type SubscriptionRow = {
  id: number;
  company_id: number;
  deal_id: number | null;
  billing: Billing;
  first_paid_month: string;
  ended_month: string | null;
  status: "active" | "cancelled";
  /** « Reprise » (owner ruling): the cancelled subscription this one continues; its months count on from that chain
   * and its earners are the chain's first subscription's deal. Absent or null: a new sale, month 1. */
  continues_subscription_id?: number | null;
};
export type PeriodRow = {
  id?: number;
  subscription_id: number;
  from_month: string;
  billing: Billing;
  package_id: number;
  package_price: number;
  addons: AddonSnapshot[];
  services: RecurringService[];
  annual_amount_paid: number | null;
};
export type ExceptionRow = {
  id?: number;
  subscription_id: number;
  month: string;
  kind: ExceptionKind;
  amount_collected: number | null;
};
export type PlanRow = {
  id: number;
  sales_id: number;
  effective_from: string;
  effective_to: string | null;
  rate_initial_pct: number;
  initial_months: number;
  rate_after_pct: number;
  /** The case grid (owner ruling « very flexible »): obtained the appointment, conducted the demo and closed. */
  full_share_pct: number;
  /** Closed while someone else conducted the demo. */
  demo_by_other_split_pct: number;
  /** Only obtained the appointment: the bonus, plus this share of each month's commission (0 by default). */
  appointment_bonus: number;
  appointment_share_pct: number;
  /** Obtained the appointment and conducted the demo, someone else closed; null = « À décider » (art. 4.5.6). */
  appointment_demo_share_pct: number | null;
  bonus_window_days: number;
  eligible_sources: readonly string[];
  employment_end: string | null;
  /** Vacation pay (75_, owner ruling 2026-10-21, LNT art. 74): 0 = not computed (the default: counted by payroll for
   * now); else 4, 6 after 3 years of continuous service; absent = 0 (the SQL default). */
  vacation_pay_pct?: number;
};
export type SaleStepsDeal = {
  id: number;
  sales_id: number | null;
  setter_sales_id: number | null;
  demo_by_sales_id: number | null;
  sale_source: string | null;
  appointment_at: string | null;
  created_at: string | null;
  demo_held_at: string | null;
  won_at: string | null;
  commission_override: {
    split_pct?: unknown;
    setter_split_pct?: unknown;
    note?: unknown;
  } | null;
};
export type CommissionData = {
  subscriptions: readonly SubscriptionRow[];
  periods: readonly PeriodRow[];
  exceptions: readonly ExceptionRow[];
  deals: readonly SaleStepsDeal[];
  plans: readonly PlanRow[];
};
export type EarningLine = {
  sales_id: number;
  subscription_id: number;
  company_id: number;
  deal_id: number;
  month: string;
  /** The 1st of the month after `month` (art. 4.2 « Déclenchement »). */
  payable_from: string;
  kind: EarningKind | "to_decide";
  mrr_collected: number;
  subscription_month: number;
  split_pct: number | null;
  rate_pct: number | null;
  /** null for « À décider ». */
  amount: number | null;
  explanation: string;
};
/** A line already on another statement of the same person (draft or paid). */
export type StatedLine = {
  /** The statement line's id (voidedDebts returns the ids it cancels). */
  id?: number;
  subscription_id: number;
  month: string;
  kind: LineKind;
  adjusts: EarningKind | null;
  amount: number | null;
  /** How a negative adjustment was settled (75_): none = still open. */
  settled?: SettlementKind | null;
};
/** public.commission_settlements.kind (75_): retenue (deducted with written consent), rembourse (paid back), abandonne
 * (waived), annule (cancelled by a later correction: the system's, review C1). */
export const SETTLEMENT_KINDS = [
  "retenue",
  "rembourse",
  "abandonne",
  "annule",
] as const;
export type SettlementKind = (typeof SETTLEMENT_KINDS)[number];
/** A line a new (or refreshed) statement gets. */
export type FilledLine = {
  subscription_id: number;
  company_id: number;
  deal_id: number | null;
  month: string;
  payable_from: string;
  kind: LineKind;
  adjusts: EarningKind | null;
  mrr_collected: number | null;
  subscription_month: number | null;
  split_pct: number | null;
  rate_pct: number | null;
  amount: number | null;
  explanation: string;
};

// ---------------------------------------------------------------------------------------------------------------- money
export const toCents = (dollars: number): number => Math.round(dollars * 100);
export const fromCents = (cents: number): number => cents / 100;
/** round(n / d) half away from zero, on integers (d > 0): Postgres numeric round. */
const divHalfUp = (n: bigint, d: bigint): bigint =>
  n >= 0n ? (2n * n + d) / (2n * d) : -((2n * -n + d) / (2n * d));
const hundredths = (pct: number): bigint => BigInt(Math.round(pct * 100));
/** round(mrr × rate % × split %, 2): a commission line (private.commission_lines). */
export const commissionAmount = (
  mrr: number,
  ratePct: number,
  splitPct: number,
): number =>
  fromCents(
    Number(
      divHalfUp(
        BigInt(toCents(mrr)) * hundredths(ratePct) * hundredths(splitPct),
        100_000_000n,
      ),
    ),
  );
/** round(annual / 12, 2): the MRR of an annual period (art. 4.7.3). */
export const annualMrr = (annual: number): number =>
  fromCents(Number(divHalfUp(BigInt(toCents(annual)), 12n)));
/** Package price + Σ qty × unit price. */
export const monthlyTotal = (
  packagePrice: number,
  addons: readonly { qty: number; unit_price: number }[],
): number =>
  fromCents(
    toCents(packagePrice) +
      addons.reduce((s, a) => s + a.qty * toCents(a.unit_price), 0),
  );
/** The annual price suggested for a monthly total: round(monthly × 12 × (100 − discount) %, 2). */
export const annualAmount = (monthly: number, discountPct: number): number =>
  fromCents(
    Number(
      divHalfUp(
        BigInt(toCents(monthly)) * 12n * (10_000n - hundredths(discountPct)),
        10_000n,
      ),
    ),
  );
const money2 = (cents: number): string => (cents / 100).toFixed(2);
const pct2 = (pct: number): string => pct.toFixed(2);

// --------------------------------------------------------------------------------------------------------------- months
export const monthStart = (iso: string): string => `${iso.slice(0, 7)}-01`;
export const addMonths = (month: string, n: number): string => {
  const total =
    Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7)) - 1 + n;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, "0")}-01`;
};
export const addDays = (date: string, n: number): string =>
  new Date(Date.parse(`${date.slice(0, 10)}T00:00:00Z`) + n * 86_400_000)
    .toISOString()
    .slice(0, 10);
/** Subscription month number (1 = first_paid_month); never reset by a plan change (art. 4.7.2). */
export const monthNumber = (first: string, month: string): number =>
  (Number(month.slice(0, 4)) - Number(first.slice(0, 4))) * 12 +
  Number(month.slice(5, 7)) -
  Number(first.slice(5, 7)) +
  1;

// ------------------------------------------------------------------------------------------------------------------ MRR
export const periodAt = (
  periods: readonly PeriodRow[],
  subscriptionId: number,
  month: string,
): PeriodRow | null =>
  periods
    .filter(
      (p) => p.subscription_id === subscriptionId && p.from_month <= month,
    )
    .reduce<PeriodRow | null>(
      (best, p) => (best === null || p.from_month > best.from_month ? p : best),
      null,
    );
/** Package + add-ons (or annual paid ÷ 12), plus the recurring custom services (both billings; spec « Corrections »). */
export const periodMrr = (p: PeriodRow): number =>
  fromCents(
    toCents(
      p.billing === "annual"
        ? annualMrr(p.annual_amount_paid ?? 0)
        : monthlyTotal(p.package_price, p.addons),
    ) + (p.services ?? []).reduce((sum, v) => sum + toCents(v.amount), 0),
  );
/** private.subscription_mrr: the MRR collected in `month`. */
export const subscriptionMrr = (
  sub: SubscriptionRow,
  periods: readonly PeriodRow[],
  exceptions: readonly ExceptionRow[],
  month: string,
): number => {
  if (
    month < sub.first_paid_month ||
    (sub.ended_month !== null && month > sub.ended_month)
  )
    return 0;
  const x = exceptions.find(
    (e) => e.subscription_id === sub.id && e.month === month,
  );
  if (x && (x.kind === "unpaid" || x.kind === "refunded")) return 0;
  if (x && x.kind === "partial") return x.amount_collected ?? 0;
  const p = periodAt(periods, sub.id, month);
  return p ? periodMrr(p) : 0;
};

// ------------------------------------------------------------------------------------------------------------- earnings
/** The person's plan in force on the month's 1st day (ruling 6). */
export const planInForce = (
  plans: readonly PlanRow[],
  salesId: number,
  month: string,
): PlanRow | null =>
  plans
    .filter(
      (p) =>
        p.sales_id === salesId &&
        p.effective_from <= month &&
        (p.effective_to === null || p.effective_to >= month),
    )
    .reduce<PlanRow | null>(
      (best, p) =>
        best === null || p.effective_from > best.effective_from ? p : best,
      null,
    );
/** The deal fields the Closer's split depends on (spec §4, art. 4.5.6). */
export type SplitDeal = Pick<
  SaleStepsDeal,
  | "sales_id"
  | "setter_sales_id"
  | "demo_by_sales_id"
  | "sale_source"
  | "commission_override"
>;
/** Who conducted the demo (owner ruling 2026-10-21, 75_: « The closer is the one that does the démo »): the recorded
 * « Démo par », else the deal's Closer, else the setter (SQL: coalesce(demo_by_sales_id, sales_id, setter_sales_id)). */
export const demoPerson = (
  d: Pick<SplitDeal, "demo_by_sales_id" | "sales_id" | "setter_sales_id">,
): number | null => d.demo_by_sales_id ?? d.sales_id ?? d.setter_sales_id;
/** The override is recorded when its split_pct is a number; it then sets every share (the Closer's split_pct, the
 * setter's setter_split_pct or none). */
const overrideSplit = (
  d: Pick<SplitDeal, "commission_override">,
): number | null =>
  typeof d.commission_override?.split_pct === "number"
    ? d.commission_override.split_pct
    : null;
const overrideSetterSplit = (d: SaleStepsDeal): number | null =>
  typeof d.commission_override?.setter_split_pct === "number"
    ? d.commission_override.setter_split_pct
    : null;
/** SQL: « dérogation » plus « : <note> » when the note, stripped of spaces (btrim), is not empty. */
const derogation = (d: SaleStepsDeal): string => {
  const raw = d.commission_override?.note;
  const note = typeof raw === "string" ? raw.replace(/^ +| +$/g, "") : "";
  return note ? `dérogation : ${note}` : "dérogation";
};
/** The plan of `salesId` that pays month `m` of a deal with `source`: in force on m's 1st, source eligible, employed. */
const payingPlan = (
  plans: readonly PlanRow[],
  salesId: number,
  m: string,
  source: string | null,
): PlanRow | null => {
  const pl = planInForce(plans, salesId, m);
  return pl &&
    (pl.employment_end === null || m <= pl.employment_end) &&
    source !== null &&
    pl.eligible_sources.includes(source)
    ? pl
    : null;
};
/** The case the contract does not settle (art. 4.5.6) for the deal page, until an admin records the split; null when
 * it is settled. Owner ruling (no case ii): only case i — appointment and demo by the setter, someone else closed — and
 * only while the setter's plan leaves that share open (`appointment_demo_share_pct` null). `setterPlan` = the setter's
 * plan in force (undefined when the caller cannot read it: the deal alone decides). */
export const undecidedReason = (
  d: SplitDeal,
  setterPlan?: Pick<PlanRow, "appointment_demo_share_pct"> | null,
): string | null => {
  if (overrideSplit(d) !== null || isClientSource(d.sale_source)) return null;
  const demo = demoPerson(d);
  const otherSetter =
    d.setter_sales_id !== null && d.setter_sales_id !== d.sales_id;
  if (!otherSetter || demo !== d.setter_sales_id) return null;
  if (setterPlan === null) return null;
  if (
    setterPlan !== undefined &&
    setterPlan.appointment_demo_share_pct !== null
  )
    return null;
  return "rendez-vous et démo par la même personne, signature par une autre";
};
const DAY = 86_400_000;
/** A continuation's chain: the first subscription (whose deal names the earners) and the months the earlier
 * subscriptions of the chain billed (first_paid_month..ended_month; first_paid_month alone without an end), as the SQL.
 * A broken chain (a missing link) gives no root. */
/** private.subscription_root: the last subscription reached by following continues_subscription_id (100 steps at
 * most; a missing link stops there), the subscription itself when it continues none. */
export const subscriptionRoot = <
  T extends Pick<SubscriptionRow, "id" | "continues_subscription_id">,
>(
  subscriptions: readonly T[],
  sub: T,
): T => {
  let root = sub;
  for (
    let depth = 0;
    root.continues_subscription_id != null && depth < 100;
    depth += 1
  ) {
    const prev = subscriptions.find(
      (x) => x.id === root.continues_subscription_id,
    );
    if (!prev) break;
    root = prev;
  }
  return root;
};
export const subscriptionChain = (
  subscriptions: readonly SubscriptionRow[],
  sub: SubscriptionRow,
): { root: SubscriptionRow | null; offset: number } => {
  let root = sub;
  let offset = 0;
  for (
    let depth = 0;
    root.continues_subscription_id != null && depth < 100;
    depth += 1
  ) {
    const prev = subscriptions.find(
      (x) => x.id === root.continues_subscription_id,
    );
    if (!prev) return { root: null, offset };
    offset += monthNumber(
      prev.first_paid_month,
      prev.ended_month ?? prev.first_paid_month,
    );
    root = prev;
  }
  return { root, offset };
};

/** private.commission_lines(until): every line payable on or before `until` (earning months up to the month before its
 * month; payable_from = month + 1 month), in the SQL's order. Rules (owner ruling: every share is a plan parameter):
 * the Closer's share = the override split_pct (« dérogation »), else full_share_pct when he conducted the demo
 * (« vente complète », or « client attribué » / « client organique » for an assigned / inbound client), else
 * demo_by_other_split_pct (« démo par une autre personne », with the same prefix for those clients: amendment 4.5.4,
 * owner ruling 2026-10-21); the setter's = the override setter_split_pct (or none), else none for an assigned or inbound
 * client, else in case i
 * appointment_demo_share_pct (« rendez-vous et démo »), else appointment_share_pct (« rendez-vous seulement »).
 * « À décider »: case i, no override, not assigned, a month where the setter's plan pays with a null
 * appointment_demo_share_pct: the Closer and the setter get a to_decide line instead. The bonus (case c) is
 * independent of the shares and of the override. */
export const commissionLines = (
  data: CommissionData,
  until: string,
): EarningLine[] => {
  const last = addMonths(monthStart(until), -1);
  const out: EarningLine[] = [];
  for (const s of [...data.subscriptions].sort((a, b) => a.id - b.id)) {
    const continuation = s.continues_subscription_id != null;
    if ((s.deal_id === null && !continuation) || s.first_paid_month > last)
      continue;
    // « Reprise »: the months count on from the chain, earned on the root subscription's deal
    const { root, offset } = subscriptionChain(data.subscriptions, s);
    if (root === null || root.deal_id === null) continue;
    const d = data.deals.find((x) => x.id === root.deal_id);
    if (!d) continue;
    const demo = demoPerson(d);
    const setter =
      d.setter_sales_id !== null && d.setter_sales_id !== d.sales_id
        ? d.setter_sales_id
        : null;
    const override = overrideSplit(d) !== null;
    const setterCaseI = setter !== null && demo === setter;
    const firstBilling =
      periodAt(data.periods, s.id, s.first_paid_month)?.billing ?? null;
    const end =
      s.ended_month !== null && s.ended_month < last ? s.ended_month : last;
    // the bonus counts only fully collected months (no exception: owner ruling)
    let fullyPaid = 0;
    let bonusMonth: string | null = null;
    let bonusAt: string | null = null;
    for (let m = s.first_paid_month; m <= end; m = addMonths(m, 1)) {
      const no = offset + monthNumber(s.first_paid_month, m);
      const mrr = subscriptionMrr(s, data.periods, data.exceptions, m);
      if (mrr <= 0) continue;
      // « À décider » this month: case i, no override, the setter's plan pays and leaves his share open
      const setterPlan =
        setter !== null &&
        setterCaseI &&
        !override &&
        !isClientSource(d.sale_source)
          ? payingPlan(data.plans, setter, m, d.sale_source)
          : null;
      // ... and only when his rate that month is above 0 (a 0 % setter has nothing to share: the Closer is case b)
      const undecided =
        setterPlan !== null &&
        setterPlan.appointment_demo_share_pct === null &&
        (no <= setterPlan.initial_months
          ? setterPlan.rate_initial_pct
          : setterPlan.rate_after_pct) > 0;
      for (const earner of [d.sales_id, setter]) {
        if (earner === null) continue;
        const pl = payingPlan(data.plans, earner, m, d.sale_source);
        if (!pl) continue;
        const rate =
          no <= pl.initial_months ? pl.rate_initial_pct : pl.rate_after_pct;
        let split: number | null = null;
        let label = "";
        if (override) {
          split =
            earner === d.sales_id ? overrideSplit(d) : overrideSetterSplit(d);
          label = derogation(d);
        } else if (earner === d.sales_id) {
          // 75_ (amendment 4.5.4, owner ruling 2026-10-21): the full share only when the Closer conducted the demo
          split =
            demo === d.sales_id
              ? pl.full_share_pct
              : pl.demo_by_other_split_pct;
          const client =
            d.sale_source === "assigned"
              ? "client attribué"
              : d.sale_source === "inbound"
                ? "client organique"
                : null;
          label =
            client === null
              ? demo === d.sales_id
                ? "vente complète"
                : "démo par une autre personne"
              : demo === d.sales_id
                ? client
                : `${client}, démo par une autre personne`;
        } else if (isClientSource(d.sale_source)) {
          split = null;
        } else if (setterCaseI) {
          split = pl.appointment_demo_share_pct;
          label = "rendez-vous et démo";
        } else {
          split = pl.appointment_share_pct;
          label = "rendez-vous seulement";
        }
        const base = {
          sales_id: earner,
          subscription_id: s.id,
          company_id: s.company_id,
          deal_id: d.id,
          month: m,
          payable_from: addMonths(m, 1),
          mrr_collected: mrr,
          subscription_month: no,
          rate_pct: rate,
        };
        if (undecided && rate > 0)
          out.push({
            ...base,
            kind: "to_decide",
            split_pct: null,
            amount: null,
            explanation: `Mois ${no} · taux ${pct2(rate)} % · À décider : rendez-vous et démo par la même personne, signature par une autre (art. 4.5.6)`,
          });
        else if (!undecided && split !== null && split > 0 && rate > 0)
          out.push({
            ...base,
            kind: "commission",
            split_pct: split,
            amount: commissionAmount(mrr, rate, split),
            explanation: `Mois ${no} · taux ${pct2(rate)} % · part ${pct2(split)} % · ${label}`,
          });
      }
      // monthly: the second fully collected month (no exception that month)
      if (
        !data.exceptions.some(
          (x) => x.subscription_id === s.id && x.month === m,
        )
      )
        fullyPaid += 1;
      if (
        firstBilling === "monthly" &&
        fullyPaid === 2 &&
        bonusMonth === null
      ) {
        bonusMonth = m;
        bonusAt = m;
      }
    }
    if (firstBilling === "annual") {
      bonusAt = addDays(s.first_paid_month, 30);
      bonusMonth = monthStart(bonusAt);
      const refunded = data.exceptions.some(
        (x) =>
          x.subscription_id === s.id &&
          x.kind === "refunded" &&
          x.month <= (bonusMonth as string),
      );
      if (
        bonusMonth > last ||
        (s.ended_month !== null && s.ended_month < bonusMonth) ||
        refunded
      )
        bonusMonth = null;
    }
    if (
      bonusMonth !== null &&
      bonusAt !== null &&
      // never on a continuation, once per deal (its first subscription only: art. 4.6.4)
      !continuation &&
      !data.subscriptions.some((y) => y.deal_id === d.id && y.id < s.id) &&
      d.setter_sales_id !== null &&
      d.setter_sales_id !== d.sales_id &&
      d.setter_sales_id !== demo &&
      d.sale_source === "rep_appointment" &&
      d.won_at !== null &&
      d.demo_held_at !== null
    ) {
      const pl = planInForce(data.plans, d.setter_sales_id, bonusMonth);
      const appt = d.appointment_at ?? d.created_at;
      const held = Date.parse(d.demo_held_at);
      if (
        pl &&
        pl.eligible_sources.includes("rep_appointment") &&
        pl.appointment_bonus > 0 &&
        (pl.employment_end === null || bonusAt <= pl.employment_end) &&
        // fixed 24 h days, independent of any time zone (= SQL make_interval(hours => 24 × days))
        Date.parse(d.won_at) <= held + pl.bonus_window_days * DAY &&
        appt !== null &&
        (Date.parse(appt) < held ||
          Date.parse(appt) < Date.parse(APPOINTMENT_RULE_FROM))
      )
        out.push({
          sales_id: d.setter_sales_id,
          subscription_id: s.id,
          company_id: s.company_id,
          deal_id: d.id,
          month: bonusMonth,
          payable_from: addMonths(bonusMonth, 1),
          kind: "bonus",
          mrr_collected: subscriptionMrr(
            s,
            data.periods,
            data.exceptions,
            bonusMonth,
          ),
          subscription_month: monthNumber(s.first_paid_month, bonusMonth),
          split_pct: null,
          rate_pct: null,
          amount: pl.appointment_bonus,
          explanation: `Prime de rendez-vous · ${firstBilling === "annual" ? "30 jours après le début (annuel)" : "2e paiement"}`,
        });
    }
  }
  return out;
};

// ------------------------------------------------------------------------------------------------------- statement fill
/** A negative adjustment's mark (owner ruling 2026-10-21, 75_, LNT art. 49): never deducted from a payout; the SQL
 * appends « · » + this text to its explanation (scripts/gavion/contractEdits.ts TO_SETTLE_SQL). */
export const TO_SETTLE_NOTE =
  "À régler avec l'employé (retenue seulement avec consentement écrit)";
/** A statement line to settle with the person (a negative adjustment): shown with its amount, outside what is paid. */
export const isToSettle = (l: {
  kind: LineKind;
  amount: number | null;
}): boolean => l.kind === "adjustment" && (l.amount ?? 0) < 0;
/** The vacation pay rate when a plan does not carry one (SQL: default 0 = not computed — owner ruling 2026-10-21: « Oublie
 * le 4 % pour le moment. Nous allons le compter à part. », counted by payroll). */
export const DEFAULT_VACATION_PAY_PCT = 0;
export type VacationPay = { pct: number; base: number; amount: number };
/** private.commission_vacation_pay (75_, owner ruling 2026-10-21): `pct` (the person's plan in force on the cutoff) of
 * what the statement pays (every amount but the lines to settle; « À décider »: none), rounded to the cent half up;
 * null without a plan. Information for payroll, never in the total. */
export const vacationPay = (
  lines: readonly { kind: LineKind; amount: number | null }[],
  pct: number | null | undefined,
): VacationPay | null => {
  // a rate of 0 = not computed (counted by payroll): no line anywhere
  if (pct === null || pct === undefined || pct === 0) return null;
  const base = lines.reduce(
    (sum, l) => sum + (isToSettle(l) ? 0 : toCents(l.amount ?? 0)),
    0,
  );
  return {
    pct,
    base: fromCents(base),
    amount: fromCents(Math.round((base * Math.round(pct * 100)) / 10_000)),
  };
};
/** private.commission_vacation_pay_text: the CSV row's explanation. */
export const vacationPayText = (v: VacationPay): string =>
  `Indemnité de vacances ${v.pct.toFixed(2)} % (pour la paie, hors total) : ${money2(toCents(v.amount))} $ sur ${money2(toCents(v.base))} $`;
/** public.commission_statement_mark_paid's employment_ended (75_, art. 4.3.2): the earliest employment end of the
 * person's plan in force in a month that a commission or bonus line of the draft pays after it; null = none. */
export const employmentEndedOn = (
  plans: readonly Pick<
    PlanRow,
    "sales_id" | "effective_from" | "effective_to" | "employment_end"
  >[],
  salesId: number,
  lines: readonly { kind: LineKind; month: string }[],
): string | null =>
  lines
    .filter((l) => l.kind === "commission" || l.kind === "bonus")
    .flatMap((l) =>
      plans
        .filter(
          (p) =>
            p.sales_id === salesId &&
            p.effective_from <= l.month &&
            (p.effective_to === null || p.effective_to >= l.month) &&
            p.employment_end !== null &&
            p.employment_end < l.month,
        )
        .map((p) => p.employment_end as string),
    )
    .reduce<string | null>(
      (min, e) => (min === null || e < min ? e : min),
      null,
    );

/** private.commission_statement_fill: new keys, « Correction » for a changed amount, « Annulation » for a vanished key
 * payable by the cutoff. « À décider » lines are left off (owner ruling 2026-10-21, 75_: not paid, not lost) and never
 * count as stated. A negative Correction or Annulation carries « · » + TO_SETTLE_NOTE (never deducted). One person's
 * lines only: `computed` and `stated` must both belong to the statement's person (keys carry no sales_id). An
 * « Annulation » whose subscription is no longer in `subscriptions` is dropped, like the SQL's join. */
export const statementFill = (
  computed: readonly EarningLine[],
  stated: readonly StatedLine[],
  cutoff: string,
  subscriptions: readonly Pick<
    SubscriptionRow,
    "id" | "company_id" | "deal_id" | "continues_subscription_id"
  >[],
): FilledLine[] => {
  const key = (s: number, m: string, k: EarningKind) => `${s}|${m}|${k}`;
  const already = new Map<
    string,
    {
      subscription_id: number;
      month: string;
      earning: EarningKind;
      cents: number;
      held: number;
      heldOpen: number;
    }
  >();
  for (const l of stated) {
    if (l.kind === "to_decide") continue;
    const earning: EarningKind =
      l.adjusts ?? (l.kind === "bonus" ? "bonus" : "commission");
    const k = key(l.subscription_id, l.month, earning);
    const a = already.get(k) ?? {
      subscription_id: l.subscription_id,
      month: l.month,
      earning,
      cents: 0,
      held: 0,
      heldOpen: 0,
    };
    // review C1: a line settled « annule » no longer counts; an open or waived negative is still held by the person
    if (l.settled !== "annule") a.cents += toCents(l.amount ?? 0);
    if (
      isToSettle(l) &&
      (l.settled === null ||
        l.settled === undefined ||
        l.settled === "abandonne")
    )
      a.held += toCents(l.amount ?? 0);
    if (isToSettle(l) && (l.settled ?? null) === null)
      a.heldOpen += toCents(l.amount ?? 0);
    already.set(k, a);
  }
  const out: FilledLine[] = [];
  const seen = new Set<string>();
  for (const c of computed) {
    const base = {
      subscription_id: c.subscription_id,
      company_id: c.company_id,
      deal_id: c.deal_id,
      month: c.month,
      payable_from: c.payable_from,
      mrr_collected: c.mrr_collected,
      subscription_month: c.subscription_month,
      split_pct: c.split_pct,
      rate_pct: c.rate_pct,
    };
    if (c.kind === "to_decide") continue;
    const k = key(c.subscription_id, c.month, c.kind);
    seen.add(k);
    const found = already.get(k);
    const due = toCents(c.amount ?? 0);
    // review C1: due more than stated first cancels what the person still holds; only the rest is paid. Re-review N1:
    // a waived debt absorbs a rise but never creates one (SQL: greatest(least(c, tot - held), tot - held_open))
    const a = found && {
      cents:
        due > found.cents
          ? Math.max(
              Math.min(due, found.cents - found.held),
              found.cents - found.heldOpen,
            )
          : found.cents,
    };
    if (!a)
      out.push({
        ...base,
        kind: c.kind,
        adjusts: null,
        amount: c.amount,
        explanation: c.explanation,
      });
    else if (due !== a.cents)
      out.push({
        ...base,
        kind: "adjustment",
        adjusts: c.kind,
        amount: fromCents(due - a.cents),
        explanation: `Correction ${c.month.slice(0, 7)} : dû ${money2(due)} $, déjà ${money2(a.cents)} $${due < a.cents ? ` · ${TO_SETTLE_NOTE}` : ""}`,
      });
  }
  for (const [k, a] of already) {
    const payable = addMonths(a.month, 1);
    if (seen.has(k) || payable > cutoff || a.cents === 0) continue;
    const s = subscriptions.find((x) => x.id === a.subscription_id);
    if (!s) continue;
    out.push({
      subscription_id: a.subscription_id,
      company_id: s.company_id,
      // the chain root's deal, as the computed lines carry (SQL: join subscription_root)
      deal_id: subscriptionRoot(subscriptions, s).deal_id,
      month: a.month,
      payable_from: payable,
      kind: "adjustment",
      adjusts: a.earning,
      mrr_collected: null,
      subscription_month: null,
      split_pct: null,
      rate_pct: null,
      amount: fromCents(-a.cents),
      explanation: `Annulation ${a.month.slice(0, 7)} : déjà ${money2(a.cents)} $${a.cents > 0 ? ` · ${TO_SETTLE_NOTE}` : ""}`,
    });
  }
  return out;
};

/** private.commission_voided_debts (review C1): the ids of the open (unsettled) negative adjustments among `paid` (the
 * person's PAID statement lines, other than the statement being paid) whose key `computed` now makes due more than
 * stated (an « annule » line not counted): « Marquer versé » closes them « annule ». */
export const voidedDebts = (
  computed: readonly EarningLine[],
  paid: readonly StatedLine[],
): number[] => {
  const earningOf = (l: StatedLine): EarningKind =>
    l.adjusts ?? (l.kind === "bonus" ? "bonus" : "commission");
  const tot = new Map<string, number>();
  const keyOf = (s: number, m: string, k: string) => `${s}|${m}|${k}`;
  for (const l of paid)
    if (l.kind !== "to_decide" && l.settled !== "annule") {
      const k = keyOf(l.subscription_id, l.month, earningOf(l));
      tot.set(k, (tot.get(k) ?? 0) + toCents(l.amount ?? 0));
    }
  return paid
    .filter((l) => {
      if (!isToSettle(l) || (l.settled ?? null) !== null || l.id === undefined)
        return false;
      const c = computed.find(
        (x) =>
          x.subscription_id === l.subscription_id &&
          x.month === l.month &&
          x.kind === earningOf(l),
      );
      const k = keyOf(l.subscription_id, l.month, earningOf(l));
      return c !== undefined && toCents(c.amount ?? 0) > (tot.get(k) ?? 0);
    })
    .map((l) => l.id as number)
    .sort((x, y) => x - y);
};
/** private.commission_deal_has_plan (owner ruling 2026-10-21: the owners take no commission and have no plan): does
 * someone on the deal (Setter, Closer, Responsable de compte, Apporteur, Démo par) have a plan for [from, to]? */
export const dealHasPlan = (
  deal: {
    sales_id: number | null;
    setter_sales_id: number | null;
    account_manager_sales_id?: number | null;
    referrer_sales_id?: number | null;
    demo_by_sales_id: number | null;
  },
  plans: readonly Pick<
    PlanRow,
    "sales_id" | "effective_from" | "effective_to"
  >[],
  from: string,
  to: string,
): boolean => {
  const people = [
    deal.sales_id,
    deal.setter_sales_id,
    deal.account_manager_sales_id ?? null,
    deal.referrer_sales_id ?? null,
    deal.demo_by_sales_id,
  ].filter((x): x is number => x !== null);
  return plans.some(
    (p) =>
      people.includes(p.sales_id) &&
      p.effective_from <= to &&
      (p.effective_to === null || p.effective_to >= from),
  );
};
/** private.commission_vacation_pay's rate for a draft (review I3): the person's plan in force on the cutoff, else his
 * last plan before it (a final statement after a departure); null = no plan. A paid statement keeps its frozen rate. */
export const vacationPlanPct = (
  plans: readonly Pick<
    PlanRow,
    "sales_id" | "effective_from" | "vacation_pay_pct"
  >[],
  salesId: number,
  cutoff: string,
): number | null => {
  const p = plans
    .filter((x) => x.sales_id === salesId && x.effective_from <= cutoff)
    .reduce<
      (typeof plans)[number] | null
    >((best, x) => (best === null || x.effective_from > best.effective_from ? x : best), null);
  return p === null ? null : (p.vacation_pay_pct ?? DEFAULT_VACATION_PAY_PCT);
};

// ------------------------------------------------------------------------------------------------------ plan guard
/** A line of a PAID statement, with the statement's person. */
export type PaidLine = { sales_id: number; month: string; kind: LineKind };
type GuardedPlan = Omit<PlanRow, "eligible_sources"> & {
  eligible_sources: readonly string[];
  created_at?: string | null;
};
/** The latest month the plan covers that a paid statement of its person holds (private.commission_plans_in_force_guard,
 * final review ruling: a plan is frozen only once money was paid under it); null = the plan is still freely edited. */
export const planPaidThrough = (
  plan: Pick<PlanRow, "sales_id" | "effective_from" | "effective_to">,
  paid: readonly PaidLine[],
): string | null =>
  paid
    .filter(
      (l) =>
        l.sales_id === plan.sales_id &&
        l.kind !== "to_decide" &&
        l.month >= plan.effective_from &&
        (plan.effective_to === null || l.month <= plan.effective_to),
    )
    .reduce<
      string | null
    >((max, l) => (max === null || l.month > max ? l.month : max), null);
/** The fields a plan paid on never changes, in the SQL guard's check order (opsParity pins it). */
export const PLAN_FROZEN_FIELDS = [
  "id",
  "created_at",
  "sales_id",
  "effective_from",
  "rate_initial_pct",
  "initial_months",
  "rate_after_pct",
  "demo_by_other_split_pct",
  "appointment_bonus",
  "full_share_pct",
  "appointment_share_pct",
  "appointment_demo_share_pct",
  "bonus_window_days",
  "eligible_sources",
  "vacation_pay_pct",
] as const;
/** The SQL guard's refusal (the plan_in_force detail) for a delete (`next` null) or an update to `next`, in the SQL's
 * check order; null = allowed. */
export const planGuardRefusal = (
  old: GuardedPlan,
  next: GuardedPlan | null,
  paid: readonly PaidLine[],
): string | null => {
  const through = planPaidThrough(old, paid);
  if (through === null) return null;
  if (next === null) return "delete";
  for (const k of PLAN_FROZEN_FIELDS) {
    const a = old[k];
    const b = next[k];
    const same = Array.isArray(a)
      ? JSON.stringify(a) === JSON.stringify(b)
      : (a ?? null) === (b ?? null) ||
        (typeof a === "number" && Number(b) === a);
    if (!same) return k;
  }
  const floor = addDays(addMonths(through, 1), -1);
  const below = (v: string | null) => (v ?? "9999-12-31") < floor;
  if (
    old.effective_to !== next.effective_to &&
    (below(old.effective_to) || below(next.effective_to))
  )
    return "effective_to";
  if (
    old.employment_end !== next.employment_end &&
    (below(old.employment_end) || below(next.employment_end))
  )
    return "employment_end";
  return null;
};

// ------------------------------------------------------------------------------------------------ sale-step defaults
/** public.deals_sale_steps_default (the R6 / booking path, when the setter is first set; never an admin's form): only
 * empty steps are filled — demo by the deal's Closer when one is set, else left empty (75_, owner ruling 2026-10-21:
 * never the setter; the Closer set later is read as the demo person); source rep_appointment; appointment = the setter's latest demo_booked call
 * on the deal, else now. */
export const saleStepsDefaults = <
  D extends Pick<
    SaleStepsDeal,
    | "sales_id"
    | "setter_sales_id"
    | "demo_by_sales_id"
    | "sale_source"
    | "appointment_at"
  >,
>(
  d: D,
  demoCallAt: string | null,
  now: string,
): D =>
  d.setter_sales_id === null
    ? d
    : {
        ...d,
        // 75_ (review I4): the Closer only, never the setter (no Closer yet: left empty, the Closer read once set)
        demo_by_sales_id: d.demo_by_sales_id ?? d.sales_id,
        sale_source: d.sale_source ?? "rep_appointment",
        appointment_at: d.appointment_at ?? demoCallAt ?? now,
      };

// ------------------------------------------------------------------------------------ statement base (owner ruling)
/** private.commission_line_masked's pattern: the explanation's « taux » and « part » segments (they reveal the base). */
export const COMMISSION_BASE_PATTERN = " · (taux|part) [0-9]+\\.[0-9]+ %";

/** private.commission_base_visible (owner ruling 2026-10-20, deal amount visibility): an admin sees the client's base on
 * every statement line; anyone else only on a line whose deal he is the Closer or Responsable de compte of (a line with
 * no deal: admins only). */
export const commissionBaseVisible = (
  deal: DealRoleFields | null | undefined,
  viewer: DealViewer,
): boolean =>
  viewer.isAdmin || (deal != null && holdsAmountRole(deal, viewer.salesId));

/** private.commission_line_masked: the line as a Setter / Apporteur only reads it on his own statement — his amount, no
 * MRR collected, split or rate, the explanation without its « taux » and « part ». */
export const maskStatementLine = <
  L extends {
    mrr_collected: number | null;
    split_pct: number | null;
    rate_pct: number | null;
    explanation: string;
  },
>(
  line: L,
): L & { base_hidden: true } => ({
  ...line,
  mrr_collected: null,
  split_pct: null,
  rate_pct: null,
  base_hidden: true,
  explanation: line.explanation.replace(
    new RegExp(COMMISSION_BASE_PATTERN, "g"),
    "",
  ),
});
